import { SnackbarOrigin } from "@mui/material/Snackbar";
import { Resource } from "@redotech/util/resource";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import { type CSSProperties, ReactNode } from "react";
import * as alertStyles from "./alert.module.css";
import { IconButton, IconButtonTheme } from "./button";
import XIcon from "./icon-old/x.svg";
import {
  enqueueNotification,
  NotificationInstance,
  popNotification,
} from "./notification-provider";

const toastActionContainerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const defaultOrigin: SnackbarOrigin = {
  horizontal: "center",
  vertical: "bottom",
};
const defaultDuration = Temporal.Duration.from({ seconds: 5 });

function close(snackbarId: SnackbarKey) {
  return (
    <IconButton
      onClick={() => closeSnackbar(snackbarId)}
      theme={IconButtonTheme.GRAY}
    >
      <XIcon />
    </IconButton>
  );
}

export function alertOnFailure(
  message?: string,
  hideErrorMessage = false,
): Resource<void> {
  return async (fn) => {
    try {
      return await fn();
    } catch (error) {
      const alertMessage = `${message ?? ""}${!hideErrorMessage && error instanceof Error && !!error.message ? `${message ? ": " : ""}${error.message}` : ""}`;
      console.error(error);
      enqueueSnackbar(alertMessage, {
        anchorOrigin: defaultOrigin,
        autoHideDuration: defaultDuration.total("milliseconds"),
        variant: "error",
        action: close,
      });
      throw error;
    }
  };
}

export function alertMessage(message: string): void {
  enqueueSnackbar(message, {
    anchorOrigin: defaultOrigin,
    autoHideDuration: defaultDuration.total("milliseconds"),
    variant: "error",
    action: close,
  });
}

export const showNotification = (instance: NotificationInstance) =>
  enqueueNotification(instance);

export const hideNotification = () => popNotification();

// for some reason, using SnackbarOrigin directly
// causes a cursed build error
interface ISnackbarOrigin {
  horizontal: "left" | "center" | "right";
  vertical: "top" | "bottom";
}

// Module-level state for dismissPrevious behavior. This is acceptable for
// toast management since toasts are inherently global UI and sequential —
// the last-write-wins semantics match the expected UX.
let lastToastKey: SnackbarKey | null = null;

export function toast(
  message: string | ReactNode,
  options: {
    variant: "error" | "info" | "success" | "warning";
    origin?: ISnackbarOrigin;
    duration?: Temporal.Duration;
    persist?: boolean;
    hideIcon?: boolean;
    shake?: boolean;
    action?: ReactNode | ((dismiss: () => void) => ReactNode);
    dismissPrevious?: boolean;
  },
): void {
  if (options.dismissPrevious && lastToastKey !== null) {
    closeSnackbar(lastToastKey);
  }

  const key = enqueueSnackbar(message, {
    anchorOrigin: options.origin ?? defaultOrigin,
    autoHideDuration: options.persist
      ? null
      : (options.duration?.total("milliseconds") ??
        defaultDuration.total("milliseconds")),
    variant: options.variant,
    action: options.action
      ? (snackbarId: SnackbarKey) => {
          const dismiss = () => closeSnackbar(snackbarId);
          return (
            <div style={toastActionContainerStyle}>
              {typeof options.action === "function"
                ? options.action(dismiss)
                : options.action}
              {close(snackbarId)}
            </div>
          );
        }
      : close,
    hideIconVariant: options.hideIcon ?? false,
    className: options.shake ? alertStyles.shake : undefined,
  });

  if (options.dismissPrevious) {
    lastToastKey = key;
  }
}
